import React, { useEffect, useRef } from 'react';
import { TableCell, TableRow, Badge, Checkbox } from '@roger/ui-components';
import {
  Loader2,
  User,
  Calendar,
  Building,
  FileText,
  XCircle,
  RefreshCw,
  Stethoscope,
  MapPin,
  UserPlus,
  Pencil,
} from 'lucide-react';
import { useState } from 'react';
import { IntakeUserAssignmentDialog } from './IntakeUserAssignmentDialog';
import type { IntakeRecord } from '../store/slices/intakeSlice';
import type { AgencyClinicResponse } from '../services/api/types.gen';
import { useWorkflowStatus } from '../hooks/useWorkflowStatus';
import StepStatusIndicator from './StepStatusIndicator';
import { useStore } from '../store';
import type { EnabledTabs } from '../constants/intakeTabs';

interface IntakeRowWithStatusProps {
  intake: IntakeRecord;
  isProcessing: boolean;
  getPatientName: (intake: IntakeRecord) => string;
  formatDate: (date: Date | string | undefined) => string;
  formatDateTime: (date: Date | string | undefined) => string;
  agencyClinics: AgencyClinicResponse[];
  agencyName?: string;
  onClick: (e: React.MouseEvent) => void;
  onWorkflowComplete?: (intakeId: string) => void;
  enabledTabs?: EnabledTabs;
  onRerun?: (intake: IntakeRecord) => void;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  onAssignComplete?: () => void;
}

const IntakeRowWithStatus: React.FC<IntakeRowWithStatusProps> = ({
  intake,
  isProcessing,
  getPatientName,
  formatDate,
  formatDateTime,
  agencyClinics,
  agencyName,
  onClick,
  onWorkflowComplete,
  enabledTabs,
  onRerun,
  showCheckbox = false,
  isSelected = false,
  onCheckboxChange,
  onAssignComplete,
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  // Get clinician name lookup from store (O(1) vs O(n*m) nested loop)
  const getClinicianNameById = useStore(state => state.getClinicianNameById);

  // Only poll workflow status if intake is processing
  const workflowStatus = useWorkflowStatus({
    intakeId: intake.id,
    enabled: isProcessing,
  });

  // Check if failed either from workflow status or from database
  const isFailed =
    (isProcessing && workflowStatus.isFailed) ||
    (intake.processing_status === 'failed' && !!intake.processing_errors);

  // Show processing/failed UI if currently processing OR if it has persisted failure state
  const showProcessingUI = isProcessing || isFailed;

  const hasCalledCompleteRef = useRef(false);

  // When workflow completes or fails, trigger a refresh of this specific intake (only once)
  useEffect(() => {
    if (
      (workflowStatus.isCompleted || workflowStatus.isFailed) &&
      onWorkflowComplete &&
      !hasCalledCompleteRef.current
    ) {
      console.log(
        '[IntakeRowWithStatus] Workflow finished for intake:',
        intake.id,
        'completed:',
        workflowStatus.isCompleted,
        'failed:',
        workflowStatus.isFailed
      );
      hasCalledCompleteRef.current = true;
      onWorkflowComplete(intake.id);
    }
  }, [
    workflowStatus.isCompleted,
    workflowStatus.isFailed,
    onWorkflowComplete,
    intake.id,
  ]);

  return (
    <TableRow
      key={intake.id}
      className={
        showProcessingUI
          ? isFailed
            ? 'bg-red-50/50 cursor-pointer opacity-60'
            : 'bg-blue-50/50 cursor-not-allowed opacity-60'
          : 'hover:bg-muted/50 cursor-pointer'
      }
      onClick={onClick}
    >
      {showCheckbox && (
        <TableCell onClick={e => e.stopPropagation()}>
          <Checkbox checked={isSelected} onCheckedChange={onCheckboxChange} />
        </TableCell>
      )}
      <TableCell>
        <div className='flex items-center gap-2'>
          <User className='h-4 w-4 text-muted-foreground' />
          <div>
            <div className='font-medium'>
              {showProcessingUI ? (
                <div className='flex items-center gap-2'>
                  {isFailed ? (
                    <XCircle className='h-3 w-3 text-red-600' />
                  ) : (
                    <Loader2 className='h-3 w-3 animate-spin' />
                  )}
                  {isFailed ? '' : 'Processing...'}
                </div>
              ) : (
                getPatientName(intake)
              )}
            </div>
            {intake.patient_dob && !showProcessingUI && (
              <div className='text-muted-foreground'>
                DOB: {formatDate(intake.patient_dob)}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        {showProcessingUI ? (
          <div className='flex items-center gap-2'>
            {isFailed ? (
              <>
                <XCircle className='h-3 w-3 text-red-600' />
                <Badge
                  variant='destructive'
                  className='flex items-center gap-1'
                >
                  <FileText className='h-3 w-3' />
                  Failed
                </Badge>
              </>
            ) : (
              <>
                <Loader2 className='h-3 w-3 animate-spin' />
                <Badge variant='secondary' className='flex items-center gap-1'>
                  <FileText className='h-3 w-3' />
                  Processing...
                </Badge>
              </>
            )}
          </div>
        ) : intake.status === 'rejected' ? (
          <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
            <div className='w-2 h-2 rounded-full bg-gray-400' />
            <span>Non-Admit</span>
          </div>
        ) : (
          <StepStatusIndicator
            tabStatuses={intake.tab_statuses}
            enabledTabs={enabledTabs}
          />
        )}
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-2'>
          <Building className='h-4 w-4 text-muted-foreground' />
          <div className='font-medium'>
            {showProcessingUI ? (
              <div className='flex items-center gap-2'>
                {isFailed ? (
                  <XCircle className='h-3 w-3 text-red-600' />
                ) : (
                  <Loader2 className='h-3 w-3 animate-spin' />
                )}
                {isFailed ? 'Failed' : 'Processing...'}
              </div>
            ) : (
              (() => {
                const clinic = agencyClinics.find(
                  c => c.id === intake.agency_clinic_id
                );
                // If clinic found and has a name, use it
                if (clinic?.name) {
                  return clinic.name;
                }
                // If only one clinic and name is empty, use agency name as fallback
                if (
                  agencyClinics.length === 1 &&
                  !agencyClinics[0].name &&
                  agencyName
                ) {
                  return agencyName;
                }
                return 'N/A';
              })()
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-2'>
          <Building className='h-4 w-4 text-muted-foreground' />
          <div>
            <div className='font-medium'>
              {showProcessingUI ? (
                <div className='flex items-center gap-2'>
                  {isFailed ? (
                    <XCircle className='h-3 w-3 text-red-600' />
                  ) : (
                    <Loader2 className='h-3 w-3 animate-spin' />
                  )}
                  {isFailed ? '' : 'Processing...'}
                </div>
              ) : (
                intake.agency_insurance?.name || 'N/A'
              )}
            </div>
            {intake.primary_member_id && !showProcessingUI && (
              <div className='text-muted-foreground'>
                ID: {intake.primary_member_id}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-2'>
          <Stethoscope className='h-4 w-4 text-muted-foreground' />
          <div className='font-medium'>
            {showProcessingUI ? (
              <div className='flex items-center gap-2'>
                {isFailed ? (
                  <XCircle className='h-3 w-3 text-red-600' />
                ) : (
                  <Loader2 className='h-3 w-3 animate-spin' />
                )}
                {isFailed ? 'Failed' : 'Processing...'}
              </div>
            ) : (
              intake.referring_physician || 'N/A'
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-2'>
          <MapPin className='h-4 w-4 text-muted-foreground' />
          <div className='font-medium'>
            {showProcessingUI ? (
              <div className='flex items-center gap-2'>
                {isFailed ? (
                  <XCircle className='h-3 w-3 text-red-600' />
                ) : (
                  <Loader2 className='h-3 w-3 animate-spin' />
                )}
                {isFailed ? 'Failed' : 'Processing...'}
              </div>
            ) : (
              intake.referral_source || 'N/A'
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div
          className='font-medium cursor-pointer group flex items-center gap-2 hover:text-primary transition-colors'
          onClick={e => {
            e.stopPropagation();
            setAssignDialogOpen(true);
          }}
        >
          {intake.assigned_to_user ? (
            <>
              <span className='truncate max-w-[150px]'>
                {intake.assigned_to_user.email}
              </span>
              <Pencil className='h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground' />
            </>
          ) : (
            <span className='text-muted-foreground flex items-center gap-1 group-hover:text-primary'>
              Assign <UserPlus className='h-3 w-3' />
            </span>
          )}
          {/* Dialog wrapped to stop propagation */}
          <div onClick={e => e.stopPropagation()} className='hidden'>
            <IntakeUserAssignmentDialog
              open={assignDialogOpen}
              onOpenChange={setAssignDialogOpen}
              intakes={[intake]}
              onSuccess={onAssignComplete}
            />
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div className='font-medium'>
            {showProcessingUI ? (
              <div className='flex items-center gap-2'>
                {isFailed ? (
                  <XCircle className='h-3 w-3 text-red-600' />
                ) : (
                  <Loader2 className='h-3 w-3 animate-spin' />
                )}
                {isFailed ? '' : 'Processing...'}
              </div>
            ) : (
              formatDate(intake.kinnser_soc_date || undefined) || 'N/A'
            )}
          </div>
          {intake.kinnser_clinician_id && !showProcessingUI && (
            <div className='text-muted-foreground'>
              {getClinicianNameById(intake.kinnser_clinician_id) ||
                intake.kinnser_clinician_id}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className='flex items-center gap-2'>
          <Calendar className='h-4 w-4 text-muted-foreground' />
          <span>{formatDateTime(intake.created_at)}</span>
          {showProcessingUI && (
            <div
              className={`ml-2 flex items-center gap-1 text-xs ${
                isFailed ? 'text-red-600' : 'text-blue-600'
              }`}
            >
              {isFailed ? (
                onRerun ? (
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      onRerun(intake);
                    }}
                    className='font-semibold flex items-center gap-1 text-red-600 cursor-pointer hover:text-red-700'
                  >
                    Rerun
                    <RefreshCw className='h-3 w-3' />
                  </span>
                ) : (
                  <>
                    <XCircle className='h-3 w-3' />
                    <span>Failed</span>
                  </>
                )
              ) : (
                <>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  <span>Processing</span>
                </>
              )}
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default IntakeRowWithStatus;
